import { globalStores } from '@/editor/stores';
import BaseModel2d from '../BaseModel2d';
import { pixelRatio2d } from '@/editor/math/const';
import * as THREE from 'three';
import { clientPosToWorldPos } from '@/editor/utils';
import { ENTITY_PRIORITY, ENTITY_RENDER_ORDER } from '@/editor/constant/entity';
import { EEntityType } from '@/editor/types';
import { EContainerType } from '@/editor/types/entity';

interface IBaseTextInfoModelParams {
  nameEl: HTMLElement;
  areaEl: HTMLElement;
  intersectable?: boolean;
  entityId?: string;
}

const material = new THREE.MeshBasicMaterial({
  color: 0xff0000,
  transparent: true,
  opacity: 0,
  side: THREE.DoubleSide,
  depthTest: false
});

export class TextInfoModel extends BaseModel2d {
  private _nameEl: HTMLElement;
  private _areaEl: HTMLElement;
  private _shapeMesh: THREE.Mesh;

  constructor({ nameEl, areaEl, intersectable = false, entityId }: IBaseTextInfoModelParams) {
    super();
    this._nameEl = nameEl;
    this._areaEl = areaEl;
    this._shapeMesh = new THREE.Mesh(this._createGeometry(), material);
    this._modelGroup.add(this._shapeMesh);

    this.setDisplayOrder(ENTITY_RENDER_ORDER.textInfo);

    if (intersectable) {
      this._modelGroup.userData.type = EEntityType.Room;
      this._modelGroup.userData.entityId = entityId;
      this._modelGroup.userData.priority = ENTITY_PRIORITY.textInfo;
      this._modelGroup.userData.containerType = EContainerType.entity;
      this._modelGroup.userData.isTextInfo = true;
      globalStores.editorStateStore.sceneInfo.scene2d.intersectableGroup.add(this._modelGroup);
      return;
    }

    globalStores.editorStateStore.sceneInfo.scene2d.scene.add(this._modelGroup);
  }

  public update() {
    const geometry = this._createGeometry();
    if (this._shapeMesh.geometry) {
      this._shapeMesh.geometry.dispose();
      this._shapeMesh.geometry = geometry ?? new THREE.BufferGeometry();
    }
  }

  public override setVisible(visible: boolean) {
    this._modelGroup.visible = visible;
  }

  public override getVisible() {
    return this._modelGroup.visible;
  }

  private _createGeometry() {
    const {
      renderer,
      scene2d: { camera }
    } = globalStores.editorStateStore.sceneInfo;
    const nameElRect = this._nameEl.getBoundingClientRect();
    const areaElRect = this._areaEl.getBoundingClientRect();
    if (nameElRect.width === 0 || nameElRect.height === 0 || areaElRect.width === 0 || areaElRect.height === 0) {
      return undefined;
    }
    const points = [
      clientPosToWorldPos({ x: nameElRect.left, y: nameElRect.bottom }, camera, renderer),
      clientPosToWorldPos({ x: nameElRect.left, y: nameElRect.top }, camera, renderer),
      clientPosToWorldPos({ x: nameElRect.right, y: nameElRect.top }, camera, renderer),
      clientPosToWorldPos({ x: nameElRect.right, y: nameElRect.bottom }, camera, renderer),
      clientPosToWorldPos({ x: areaElRect.right, y: areaElRect.top }, camera, renderer),
      clientPosToWorldPos({ x: areaElRect.right, y: areaElRect.bottom }, camera, renderer),
      clientPosToWorldPos({ x: areaElRect.left, y: areaElRect.bottom }, camera, renderer),
      clientPosToWorldPos({ x: areaElRect.left, y: areaElRect.top }, camera, renderer)
    ].map((pos) => new THREE.Vector2(pos.x / pixelRatio2d, pos.y / pixelRatio2d));

    return new THREE.ShapeGeometry(new THREE.Shape(points));
  }
}
